
import React from 'react';
import { Card } from "@/components/ui/card";
import { Match } from '../../types/head-to-head';
import { ProgressBar } from '@/components/league-table/stats/components/ProgressBar';

interface ShotsStatsCardProps {
  matches: Match[];
  homeTeam?: string;
  awayTeam?: string;
}

export const ShotsStatsCard: React.FC<ShotsStatsCardProps> = ({ matches, homeTeam = 'Home Team', awayTeam = 'Away Team' }) => {
  const calculateShotsStats = () => {
    const thresholds = [1.5, 2.5, 3.5, 4.5, 5.5, 6.5, 7.5, 8.5];
    const stats = thresholds.map(threshold => {
      const homeOver = matches.filter(match => {
        const shots = match.HomeTeam === homeTeam ? parseInt(match.HST) || 0 : parseInt(match.AST) || 0;
        return shots > threshold;
      }).length;

      const awayOver = matches.filter(match => {
        const shots = match.HomeTeam === awayTeam ? parseInt(match.HST) || 0 : parseInt(match.AST) || 0;
        return shots > threshold;
      }).length;

      return {
        label: `>${threshold}`,
        homeValue: homeOver,
        awayValue: awayOver,
        total: matches.length || 1 // Prevent division by zero
      };
    });

    return stats;
  };

  const calculateAverages = () => {
    const stats = matches.reduce((acc, match) => {
      if (match.HomeTeam === homeTeam) {
        acc.homeShots += parseInt(match.HS) || 0;
        acc.homeShotsOnTarget += parseInt(match.HST) || 0;
      } else if (match.AwayTeam === homeTeam) {
        acc.homeShots += parseInt(match.AS) || 0;
        acc.homeShotsOnTarget += parseInt(match.AST) || 0;
      }

      if (match.HomeTeam === awayTeam) {
        acc.awayShots += parseInt(match.HS) || 0;
        acc.awayShotsOnTarget += parseInt(match.HST) || 0;
      } else if (match.AwayTeam === awayTeam) {
        acc.awayShots += parseInt(match.AS) || 0;
        acc.awayShotsOnTarget += parseInt(match.AST) || 0;
      }

      return acc;
    }, {
      homeShots: 0,
      homeShotsOnTarget: 0,
      awayShots: 0,
      awayShotsOnTarget: 0
    });
    
    const matchCount = matches.length || 1; // Prevent division by zero
    const homeAccuracy = stats.homeShots > 0 ? (stats.homeShotsOnTarget / stats.homeShots) * 100 : 0;
    const awayAccuracy = stats.awayShots > 0 ? (stats.awayShotsOnTarget / stats.awayShots) * 100 : 0;

    return {
      home: {
        shots: (stats.homeShots / matchCount).toFixed(1),
        shotsOnTarget: (stats.homeShotsOnTarget / matchCount).toFixed(1),
        accuracy: isNaN(homeAccuracy) ? '0' : homeAccuracy.toFixed(1)
      },
      away: {
        shots: (stats.awayShots / matchCount).toFixed(1),
        shotsOnTarget: (stats.awayShotsOnTarget / matchCount).toFixed(1),
        accuracy: isNaN(awayAccuracy) ? '0' : awayAccuracy.toFixed(1)
      }
    };
  };

  const stats = calculateShotsStats();
  const averages = calculateAverages();

  return (
    <Card className="p-4">
      <h3 className="text-lg font-semibold mb-4">Team Shots On Target</h3>
      <div className="grid grid-cols-11 items-center gap-2 mb-6">
        <div className="col-span-2 text-right text-blue-600 text-sm font-medium space-y-1">
          <div>Shots: {averages.home.shots}</div>
          <div>On Target: {averages.home.shotsOnTarget}</div>
          <div>Accuracy: {averages.home.accuracy}%</div>
        </div>
        <div className="col-span-3">
        </div>
        <div className="col-span-1 text-center text-sm font-medium">
          Average
        </div>
        <div className="col-span-3">
        </div>
        <div className="col-span-2 text-left text-red-600 text-sm font-medium space-y-1">
          <div>Shots: {averages.away.shots}</div>
          <div>On Target: {averages.away.shotsOnTarget}</div>
          <div>Accuracy: {averages.away.accuracy}%</div>
        </div>
      </div>
      <div className="space-y-4">
        {stats.map(({ label, homeValue, awayValue, total }) => {
          const homePercentage = total > 0 ? ((homeValue / total) * 100) : 0;
          const awayPercentage = total > 0 ? ((awayValue / total) * 100) : 0;
          const formattedHomePercentage = isNaN(homePercentage) ? '0' : homePercentage.toFixed(1);
          const formattedAwayPercentage = isNaN(awayPercentage) ? '0' : awayPercentage.toFixed(1);
          
          return (
            <div key={label} className="space-y-2">
              <div className="grid grid-cols-11 items-center gap-2">
                <div className="col-span-2 text-right text-blue-600 text-sm whitespace-nowrap">
                  {homeValue || 0} ({formattedHomePercentage}%)
                </div>
                <div className="col-span-3 relative">
                  <ProgressBar percentage={formattedHomePercentage} color="bg-blue-500" direction="left" />
                </div>
                <div className="col-span-1 text-center text-sm">{label}</div>
                <div className="col-span-3 relative">
                  <ProgressBar percentage={formattedAwayPercentage} color="bg-red-500" direction="right" />
                </div>
                <div className="col-span-2 text-left text-red-600 text-sm whitespace-nowrap">
                  {awayValue || 0} ({formattedAwayPercentage}%)
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
